<template>
  <rmr-modal
    :value="value"
    variant="form"
    :icon="icons.mdiBookOpenPageVariantOutline"
    eyebrow="Guide de lecture"
    title="Comprendre le reporting social"
    subtitle="D'où viennent les publications et leurs chiffres, et ce que change le mode « vues » / « créées »."
    max-width="900"
    content-class="social-help"
    @input="$emit('input', $event)"
    @close="$emit('input', false)"
  >
    <template #body>
      <v-card-text class="social-help__body">
        <section class="sh-section">
          <div class="sh-kicker">La source</div>
          <h3 class="sh-h3">Tout vient de <em>Nonli</em>, chaque matin</h3>
          <p class="sh-p">
            Vers 3h30, la collecte récupère les comptes sociaux, les
            publications des cinq derniers jours, puis rafraîchit les compteurs
            de toutes les publications que Nonli a mises à jour dans les
            dernières 24 heures. Une publication continue donc de bouger pendant
            des semaines après sa sortie. La colonne
            <strong>Compteurs mis à jour</strong> date le dernier relevé.
          </p>
          <p class="sh-note">
            Un compte dont le <em>listening</em> n'est pas activé chez Nonli ne
            remonte pas de compteurs détaillés : ses publications apparaissent
            sans métriques, dans les deux modes.
          </p>
        </section>

        <section class="sh-section">
          <div class="sh-kicker">Le filtre « Publications »</div>
          <h3 class="sh-h3">
            <em>Créées</em> sur la période, ou <em>vues</em> sur la période
          </h3>
          <div class="sh-modes">
            <div class="sh-mode">
              <div class="sh-mode__title">Créées sur la période</div>
              <p class="sh-p">
                Les publications <strong>publiées</strong> entre les deux dates,
                avec leurs compteurs <strong>vie entière</strong> : tout ce
                qu'elles ont fait depuis leur sortie jusqu'au dernier relevé.
              </p>
              <p class="sh-note">
                Une publication du 25 août consultée le 24 septembre affiche un
                mois d'activité, pas seulement ce qu'elle a fait en août.
              </p>
            </div>
            <div class="sh-mode">
              <div class="sh-mode__title">Vues sur la période</div>
              <p class="sh-p">
                Les publications <strong>actives</strong> entre les deux dates,
                quelle que soit leur date de sortie, avec leurs compteurs
                <strong>sommés sur la période</strong> jour par jour.
              </p>
              <p class="sh-note">
                La colonne <strong>Jours actifs (période)</strong> indique sur
                combien de jours de la période la publication a eu de
                l'activité.
              </p>
            </div>
          </div>
        </section>

        <section class="sh-section">
          <div class="sh-kicker">Les limites du mode « vues »</div>
          <h3 class="sh-h3">Seuls les compteurs additifs se somment</h3>
          <p class="sh-p">
            Clics, impressions, reach, engagement, réactions, commentaires,
            partages, vues vidéo et photo, monétisation… s'additionnent jour par
            jour. Les <strong>ratios et moyennes</strong> (CTR, engagement par
            minute, taux de complétion, taux de surperformance, durée moyenne de
            visionnage) n'ont pas de sens sommés : ils restent vides en mode «
            vues » et ne sont lisibles qu'en mode « créées ».
          </p>
          <p class="sh-note">
            Les journées d'une publication sont figées passé deux semaines ; la
            journée en cours n'est disponible que le lendemain matin.
          </p>
        </section>

        <section class="sh-section">
          <div class="sh-kicker">Cohérence avec la page Revenus</div>
          <h3 class="sh-h3">
            Le CA social est un CA <em>généré</em> sur la période
          </h3>
          <p class="sh-p">
            La page Revenus sociaux somme, par compte Facebook et par mois, la
            monétisation journalière de ces mêmes publications. Le mode « vues »
            sur une période mensuelle, filtré sur Facebook, redonne donc le CA
            du mois de cette page ; le mode « créées » ne le redonne pas.
          </p>
        </section>
      </v-card-text>
    </template>
  </rmr-modal>
</template>

<script>
import { mdiBookOpenPageVariantOutline } from "@mdi/js";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

export default {
  name: "SocialReportingHelpModal",
  components: { RmrModal },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: { mdiBookOpenPageVariantOutline },
    };
  },
};
</script>

<style lang="scss" scoped>
.social-help__body {
  display: grid;
  gap: 14px;
  padding: 20px 24px 24px !important;
  background: $rmr-surface-2;
  color: $rmr-text-base;
  font-size: 14px;
  line-height: 1.65;
}

.sh-section {
  min-width: 0;
  padding: 20px 22px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
  box-shadow: $rmr-shadow-card;
}

.sh-kicker {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  margin-bottom: 10px;
  border-radius: $rmr-radius-pill;
  background: $rmr-indigo-bg;
  color: $rmr-indigo-dark;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.sh-h3 {
  font-size: 18px;
  font-weight: 800;
  color: $rmr-text-primary;
  line-height: 1.35;
  margin: 0 0 10px;

  em {
    font-style: normal;
    color: $rmr-indigo;
  }
}

.sh-p {
  margin: 0;
  max-width: 82ch;
  color: $rmr-text-medium;

  strong {
    color: $rmr-text-primary;
    font-weight: 700;
  }
}

.sh-note {
  max-width: 82ch;
  margin: 14px 0 0;
  padding: 9px 12px;
  border-left: 3px solid $rmr-indigo-border;
  border-radius: 0 $rmr-radius-sm $rmr-radius-sm 0;
  background: $rmr-surface-2;
  color: $rmr-text-secondary;
  font-size: 13px;
  line-height: 1.55;

  em {
    font-style: normal;
    color: $rmr-indigo;
  }
}

.sh-modes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.sh-mode {
  min-width: 0;
  padding: 16px;
  border: 1px solid $rmr-indigo-border;
  border-radius: $rmr-radius-lg;
  background: $rmr-indigo-bg;

  &:nth-child(2) {
    border-color: $rmr-sessions-tint;
    background: $rmr-sessions-soft;
  }

  &__title {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 800;
    color: $rmr-text-primary;
  }

  .sh-note {
    margin-top: 12px;
    padding: 10px 0 0;
    border: 0;
    border-top: 1px solid $rmr-border-hover;
    border-radius: 0;
    background: transparent;
  }
}

@media (max-width: 700px) {
  .social-help__body {
    padding: 14px !important;
  }

  .sh-section {
    padding: 16px;
  }

  .sh-modes {
    grid-template-columns: 1fr;
  }
}
</style>
